import { flowIdeas, isApiError } from '@repletoryreach/sdk-api';
import type { Translate } from '../../../../i18n/I18nProvider';

// Suggest flows with AI (30 October 2026): the rules behind the page, kept free of React so they are tested on their own.

export const IDEAS_KEY = 'flow-ideas';
export const MIN_COUNT = 3;
export const MAX_COUNT = 20;
/** The languages a flow can speak to customers, in the order the settings offer them. */
export const FLOW_LANGUAGES = ['en', 'af', 'zu', 'xh', 'st', 'tn', 'nso', 'pt', 'fr'] as const;
export const TONES: flowIdeas.FlowIdeaTone[] = ['friendly', 'formal', 'playful'];
export const PRIORITIES: flowIdeas.FlowIdeaProposal['priority'][] = ['high', 'medium', 'low'];

/** The AI is at work on this set: the page asks again every 2 seconds. */
export const isBusy = (set?: { status: flowIdeas.FlowIdeaSetStatus } | null) => set?.status === 'thinking' || set?.status === 'building';

/** Suggestions still on offer: not removed, not yet sent to be built. */
export const offered = (items: flowIdeas.FlowIdeaItem[]) => items.filter((i) => i.status === 'proposed');

/** Items that have been sent to be built, in any state since. */
export const sentToBuild = (items: flowIdeas.FlowIdeaItem[]) =>
  items.filter((i) => i.status === 'queued' || i.status === 'building' || i.status === 'created' || i.status === 'failed' || i.status === 'skipped');

/**
 * The picks after new suggestions arrive: one never seen before is ticked when it is grounded; one the person has
 * already seen keeps whatever they chose; one no longer offered drops out.
 */
export function nextPicks(items: flowIdeas.FlowIdeaItem[], picked: ReadonlySet<number>, seen: ReadonlySet<number>): { picked: Set<number>; seen: Set<number> } {
  const open = offered(items);
  const nextSeen = new Set(seen);
  const next = new Set<number>();
  for (const item of open) {
    if (!seen.has(item.n)) {
      nextSeen.add(item.n);
      if (item.pick_by_default) next.add(item.n);
    } else if (picked.has(item.n)) next.add(item.n);
  }
  return { picked: next, seen: nextSeen };
}

/** The model a code names, or the standard one. */
export const modelOf = (options: flowIdeas.FlowIdeaOptions | undefined, code: string) =>
  options?.models.find((m) => m.code === code) ?? options?.models.find((m) => m.is_default) ?? options?.models[0];

/** What building the picked suggestions costs: their own estimates, or the model's per-flow price when one has none. */
export function buildTotal(items: flowIdeas.FlowIdeaItem[], picked: ReadonlySet<number>, model?: flowIdeas.FlowIdeaModel): { minor: number | null; tokens: number | null } {
  let minor = 0;
  let tokens = 0;
  let priced = true;
  let counted = true;
  for (const item of items) {
    if (!picked.has(item.n)) continue;
    const m = item.build_minor ?? model?.build_minor ?? null;
    if (m == null) priced = false; else minor += m;
    if (item.build_tokens == null) counted = false; else tokens += item.build_tokens;
  }
  return { minor: priced ? minor : null, tokens: counted ? tokens : null };
}

/**
 * How many of the picked flows the plan has room for: all of them when the plan has no limit. An entry point (a
 * suggestion that starts as the welcome, returning customers, anything else or after resolution flow) does not count
 * towards the plan (30 October 2026), so it always fits; the service has the last word when it builds.
 */
export function fitsPlan(items: readonly { proposal: Pick<flowIdeas.FlowIdeaProposal, 'starts'> }[], room: number | null | undefined): number {
  if (room == null) return items.length;
  const free = items.filter((i) => i.proposal.starts.type === 'entry').length;
  return free + Math.max(0, Math.min(items.length - free, room));
}

/**
 * Built improvements whose old version is not replaced yet (2 November 2026): what Replace all replaces. Not one that
 * another copy of the same old version already replaced (superseded), and not one whose copy is gone.
 */
export const toReplace = (items: flowIdeas.FlowIdeaItem[]) =>
  items.filter((i) => i.status === 'created' && !!i.original_flow_id && !!i.flow_id && !i.replaced_at && !i.superseded);

/** Built so far, of those sent: created, failed or skipped. */
export function buildProgress(items: flowIdeas.FlowIdeaItem[]): { done: number; total: number; created: number } {
  const sent = sentToBuild(items);
  const done = sent.filter((i) => i.status === 'created' || i.status === 'failed' || i.status === 'skipped').length;
  return { done, total: sent.length, created: sent.filter((i) => i.status === 'created').length };
}

/** The suggestions as the filters leave them. */
export function filterItems(items: flowIdeas.FlowIdeaItem[], goal: string, priority: string): flowIdeas.FlowIdeaItem[] {
  return items.filter((i) => (!goal || i.proposal.goal === goal) && (!priority || i.proposal.priority === priority));
}

/** The new assistant message whose `build` numbers the page has not acted on yet, if any. */
export function buildRequest(messages: flowIdeas.FlowIdeaMessage[], handled: number): flowIdeas.FlowIdeaMessage | null {
  const last = [...messages].reverse().find((m) => m.role === 'assistant');
  return last && last.n > handled && last.build.length > 0 ? last : null;
}

/** Whether the last assistant message left questions unanswered (no message from the person since). */
export function hasOpenQuestions(messages: flowIdeas.FlowIdeaMessage[]): boolean {
  const last = messages.at(-1);
  return !!last && last.role === 'assistant' && last.questions.length > 0;
}

/** The settings in one line, for the summary above the start: what the AI uses, how many, how detailed, where and how. */
export function settingsSummary(t: Translate, s: flowIdeas.FlowIdeaSettings, options?: flowIdeas.FlowIdeaOptions, insightsDate?: string): string[] {
  const parts: string[] = [];
  if (s.use_knowledge) parts.push(s.documents.length > 0 ? t('flowIdeas.summary.documents', { count: s.documents.length }) : t('flowIdeas.summary.knowledge'));
  if (s.use_insights && options?.insights && insightsDate) parts.push(t('flowIdeas.summary.insights', { date: insightsDate }));
  parts.push(t('flowIdeas.summary.count', { count: s.count }));
  parts.push(t(`flowIdeas.detail.${s.detail}.name`));
  if (s.channels.length > 0) parts.push(s.channels.map((c) => t(`flowIdeas.channel.${c}`)).join(', '));
  parts.push(t(`flowIdeas.lang.${s.language}`, s.language));
  parts.push(t(`flowIdeas.tone.${s.tone}`));
  const safety = [s.offer_person, s.office_hours, s.consent].filter(Boolean).length;
  parts.push(t(safety === 3 ? 'flowIdeas.summary.safetyOn' : safety === 0 ? 'flowIdeas.summary.safetyOff' : 'flowIdeas.summary.safetySome'));
  return parts;
}

/** A line of the AI's live activity, in the reader's words. */
export function activityText(t: Translate, a: flowIdeas.FlowIdeaActivity, formatDate: (iso: string) => string): string {
  const p = a.params ?? {};
  switch (a.key) {
    case 'knowledge': return t('flowIdeas.activity.knowledge', { count: p.count ?? 0 });
    case 'insights': return t('flowIdeas.activity.insights', { date: p.date ? formatDate(String(p.date)) : '' });
    case 'flows': return t('flowIdeas.activity.flows', { count: p.count ?? 0 });
    case 'apis': return t('flowIdeas.activity.apis', { count: p.count ?? 0 });
    case 'writing': return p.total && p.from
      ? t('flowIdeas.activity.writingRange', { from: p.from, to: p.to ?? p.total, total: p.total })
      : t('flowIdeas.activity.writing');
    case 'folding': return t('flowIdeas.activity.folding');
    case 'building': return t('flowIdeas.activity.building', { name: p.name ?? '', n: p.n ?? 1, total: p.total ?? 1 });
    case 'checking': return p.name ? t('flowIdeas.activity.checkingNamed', { name: p.name }) : t('flowIdeas.activity.checking');
    // After a build (2 November 2026): steps in the business's switched-on flows that lead nowhere.
    case 'looseEnds': return t('flowIdeas.activity.looseEnds', { count: p.count ?? 0 });
    default: return String(a.key);
  }
}

/** A warning on a suggestion, in the reader's words. */
export function flagText(t: Translate, f: flowIdeas.FlowIdeaFlag): string {
  const flow = f.flow_name ?? '';
  switch (f.code) {
    case 'not_grounded': return t('flowIdeas.flag.notGrounded');
    case 'fact_not_found': return t('flowIdeas.flag.factNotFound', { detail: f.detail });
    case 'unknown_document': return t('flowIdeas.flag.unknownDocument', { detail: f.detail });
    case 'unknown_api': return t('flowIdeas.flag.unknownApi', { detail: f.detail });
    case 'unknown_flow': return t('flowIdeas.flag.unknownFlow', { detail: f.detail });
    case 'keyword_clash': return flow ? t('flowIdeas.flag.keywordClash', { detail: f.detail, flow }) : t('flowIdeas.flag.keywordClashAny', { detail: f.detail });
    case 'overlaps': return flow ? t('flowIdeas.flag.overlaps', { flow }) : t('flowIdeas.flag.overlapsAny');
    case 'entry_taken': return flow ? t('flowIdeas.flag.entryTaken', { flow }) : t('flowIdeas.flag.entryTakenAny');
    default: return f.detail;
  }
}

/** The memory meter's fill, 0 to 100. */
export const memoryPct = (m?: flowIdeas.FlowIdeaMemory | null) => (m && m.budget_tokens > 0 ? Math.min(100, Math.round((m.used_tokens / m.budget_tokens) * 100)) : 0);

/** What an Insights report's gaps say, as ideas the person can read and edit: one line each. */
export function gapsText(t: Translate, report: { sections: { gaps: { theme: string; count: number; why: string }[]; documents: { title: string }[] } | null }): string {
  const gaps = report.sections?.gaps ?? [];
  const docs = report.sections?.documents ?? [];
  const lines = gaps.map((g) => t('flowIdeas.fromInsights.gap', { theme: g.theme, count: g.count, why: g.why }).trim());
  for (const d of docs) lines.push(t('flowIdeas.fromInsights.document', { title: d.title }));
  return lines.join('\n');
}

/** The console's language as the set's: Afrikaans or English. */
export const setLanguage = (locale: string): 'en' | 'af' => (locale.toLowerCase().startsWith('af') ? 'af' : 'en');

/** What went wrong, in the business's words. */
export function ideasErrorText(t: Translate, e: unknown): string {
  if (isApiError(e)) {
    if (e.code === flowIdeas.INSUFFICIENT_CREDITS || e.status === 402) return t('flowIdeas.error.credits');
    if (e.code === flowIdeas.SET_BUSY) return t('flowIdeas.error.busy');
    if (e.code === flowIdeas.TOO_BIG || e.status === 413) return t('flowIdeas.error.tooBig');
    if (e.code === flowIdeas.PLAN_LIMIT_REACHED) return t('flowIdeas.error.planLimit');
    if (e.code === flowIdeas.MODEL_NOT_AVAILABLE) return t('flowIdeas.error.model');
    // Replacing an old version and putting it back (2 November 2026).
    if (e.code === 'NOT_AN_IMPROVEMENT') return t('flowIdeas.error.notImprovement');
    if (e.code === 'FLOW_GONE') return t('flowIdeas.error.flowGone');
    if (e.code === 'NOTHING_TO_RESTORE') return t('flowIdeas.error.nothingToRestore');
    if (e.code === 'ALREADY_RESTORED') return t('flowIdeas.error.alreadyRestored');
    if (e.code === 'TOO_LATE') return t('flowIdeas.error.tooLate');
    if (e.code === 'PLAN_LIMIT') return t('flowIdeas.error.planLimitRestore');
    if (e.status === 403) return t('flowIdeas.error.forbidden');
    if (e.message) return e.message;
  }
  return t('common.unknownError');
}
